import api from "./api";
import {
  ok, getMe, cycleById, listCyclesCached, isStageOpen, recordDetail, listRecords, splitItems, totalWeight,
  employeeFromRecord, getLookupsCached, sheetStatusOf, selfStatusOf, managerStatusOf, resultStatusOf,
  ratingConfigFor, estimatePerformance, performanceFromResult, goalCompletionOf, recordSection, pMap, toneForLabel,
} from "./core";
import { getPerformanceResults } from "./resultApi";
import { countPendingProgress } from "./progressApprovalApi";

const APPROVED = ["Approved", "In Progress", "Completed", "Closed"];

export async function getEmployeeDashboard(cycleId) {
  const { data: mine } = await api.get("/dashboard", { params: { view: "employee" } });
  const summary = mine.find((r) => String(r.cycle_id) === String(cycleId));
  if (!summary) throw new Error("You are not part of this performance cycle yet. HR adds employees to a cycle from Performance cycles.");
  const record = await recordDetail(summary.performance_record_id);
  const cycle = await cycleById(cycleId);
  const config = await ratingConfigFor(cycle);
  const { goals, kras, kpis } = splitItems(record);
  const sheetStatus = sheetStatusOf(record);
  const selfStatus = selfStatusOf(record);
  const resultStatus = resultStatusOf(record);

  let released = null;
  if (["RELEASED", "ACKNOWLEDGED"].includes(record.record_status)) {
    const results = await recordSection(record.performance_record_id, "results", []);
    released = results.find((r) => r.released_at) || null;
  }
  const estimate = estimatePerformance({ goals, kras, kpis }, config, resultStatus);
  const perf = released ? performanceFromResult(released, config.bands) : null;
  const approvedGoals = goals.filter((g) => APPROVED.includes(g.status));

  const pendingActions = [];
  if (["Not Started", "Draft", "Changes Requested"].includes(sheetStatus) && isStageOpen(cycle, "goalSetting")) {
    pendingActions.push({ title: sheetStatus === "Changes Requested" ? "Update and resubmit your goals" : "Set your goals", detail: `${totalWeight(goals)}% of 100% weightage assigned`, link: "/my-goals", tone: "warning" });
  }
  if (record.current_stage === "TRACKING" && approvedGoals.length) {
    pendingActions.push({ title: "Keep goal progress up to date", detail: `${approvedGoals.length} approved goals`, link: "/my-goals", tone: "info" });
  }
  if (selfStatus !== "Submitted" && isStageOpen(cycle, "selfAssessment") && ["TRACKING", "SELF_ASSESSMENT"].includes(record.current_stage)) {
    pendingActions.push({ title: "Complete your self assessment", detail: "Rate your goals and enter KRA/KPI actuals", link: "/self-assessment", tone: "primary" });
  }
  if (record.record_status === "RELEASED" && cycle?.workflow?.acknowledgment_required) {
    pendingActions.push({ title: "Acknowledge your result", detail: "HR has released your final rating", link: "/my-results", tone: "success" });
  }

  return ok({
    recordId: record.performance_record_id,
    sheetStatus,
    selfStatus,
    resultStatus,
    stages: (cycle?.stages || []).map((st) => {
      const done = {
        goalSetting: ["APPROVED", "CLOSED"].includes(record.goal_plan?.plan_status),
        selfAssessment: Boolean(record.self_submitted_at),
        managerReview: Boolean(record.manager_submitted_at),
        hrFinalization: Boolean(record.current_result_revision_id),
      }[st.key];
      return done ? { ...st, status: "done" } : st;
    }),
    goalCount: goals.length,
    goalWeightage: totalWeight(goals),
    goalCompletion: goalCompletionOf(goals),
    completedGoals: goals.filter((g) => g.status === "Completed" || Number(g.achievement) >= 100).length,
    goals,
    pendingActions,
    scores: {
      kraScore: perf ? perf.kraScore : estimate.kraScore,
      kpiScore: perf ? perf.kpiScore : estimate.kpiScore,
      finalScore: perf ? perf.finalScore : null,
      provisionalScore: perf ? null : estimate.finalScore,
      rating: perf ? perf.rating : null,
      ratingTone: perf ? perf.ratingTone : "neutral",
    },
  });
}

export async function getManagerDashboard(cycleId) {
  const [me, lookups, cycle] = await Promise.all([getMe(), getLookupsCached(), cycleById(cycleId)]);
  const config = await ratingConfigFor(cycle);
  const { data: reviewing } = await api.get("/dashboard", { params: { view: "manager" } }).catch(() => ({ data: [] }));
  let ids = reviewing.filter((r) => String(r.cycle_id) === String(cycleId)).map((r) => r.performance_record_id);
  if (!reviewing.length) {
    ids = (await listRecords(cycleId)).filter((r) => r.review_manager_employee_id === me.employee_id && r.employee_id !== me.employee_id).map((r) => r.performance_record_id);
  }
  const records = await pMap(ids, (id) => recordDetail(id), 6);
  const team = await pMap(records, async (record) => {
    const { goals, kras, kpis } = splitItems(record);
    const resultStatus = resultStatusOf(record);
    let performance = estimatePerformance({ goals, kras, kpis }, config, resultStatus);
    if (record.current_result_revision_id) {
      const results = await recordSection(record.performance_record_id, "results", []);
      if (results[0]) performance = performanceFromResult(results[0], config.bands, resultStatus);
    }
    return {
      recordId: record.performance_record_id,
      employee: employeeFromRecord(record, lookups),
      sheetStatus: sheetStatusOf(record),
      goalCount: goals.length,
      goalCompletion: goalCompletionOf(goals),
      selfStatus: selfStatusOf(record),
      managerStatus: managerStatusOf(record),
      resultStatus,
      performance,
    };
  }, 6);
  const completions = team.map((t) => t.goalCompletion).filter((v) => v !== null && v !== undefined);
  return ok({
    teamSize: team.length,
    pendingApprovals: team.filter((t) => t.sheetStatus === "Pending Approval").length,
    selfPending: team.filter((t) => t.selfStatus !== "Submitted").length,
    managerPending: team.filter((t) => t.selfStatus === "Submitted" && t.managerStatus !== "Submitted").length,
    pendingProgress: (await countPendingProgress(cycleId)).count, // [NEW]
    avgCompletion: completions.length ? Math.round((completions.reduce((a, b) => a + b, 0) / completions.length) * 10) / 10 : null,
    managerReviewOpen: isStageOpen(cycle, "managerReview"),
    team,
  });
}

const avg = (values) => {
  const list = values.filter((v) => v !== null && v !== undefined);
  return list.length ? Math.round((list.reduce((a, b) => a + Number(b), 0) / list.length) * 10) / 10 : null;
};
const percent = (rows, total, test) => (total ? Math.round((rows.filter(test).length / total) * 100) : 0);

export async function getHrDashboard(cycleId, { departmentId, employeeFilter } = {}) {
  const [{ data: hr }, { data: allRows }, cycle, companyName] = await Promise.all([
    api.get("/dashboard", { params: { view: "hr", cycle_id: cycleId } }),
    getPerformanceResults(cycleId, { departmentId }),
    cycleById(cycleId),
    api.get("/company-settings").then((r) => r.data?.company_name || null).catch(() => null),
  ]);
  const config = await ratingConfigFor(cycle);
  // [NEW] employeeFilter(employee) applies the Business Unit / Department / Location / Cost Centre / Legal Entity filters
  const rows = employeeFilter ? allRows.filter((r) => employeeFilter(r.employee)) : allRows;
  const eligible = rows.filter((r) => r.eligibility === "ELIGIBLE");
  const total = eligible.length || 1;
  const finalized = eligible.filter((r) => r.resultStatus === "Finalized");
  const departments = Object.values(eligible.reduce((acc, r) => {
    const key = r.employee.departmentId || "none";
    acc[key] = acc[key] || { id: key, name: r.employee.departmentName, headcount: 0, scores: [] };
    acc[key].headcount += 1;
    if (r.resultStatus === "Finalized" && r.performance.finalScore !== null) acc[key].scores.push(r.performance.finalScore);
    return acc;
  }, {})).map((d) => ({ id: d.id, name: d.name, headcount: d.headcount, avgScore: avg(d.scores) }));
  const sheetStatuses = ["Not Started", "Draft", "Pending Approval", "Changes Requested", "Approved"];
  return ok({
    rows: eligible,
    companyName,
    headcount: eligible.length,
    departments,
    goalStatus: sheetStatuses.map((status) => ({ status, count: eligible.filter((r) => r.sheetStatus === status).length })),
    ratingDistribution: [...config.bands].map((b) => ({ rating: b.label, tone: b.tone, count: finalized.filter((r) => r.performance.rating === b.label && r.performance.finalScore !== null).length })),
    completion: {
      goalsApproved: percent(eligible, total, (r) => r.sheetStatus === "Approved"),
      selfSubmitted: percent(eligible, total, (r) => r.selfStatus === "Submitted"),
      managerSubmitted: percent(eligible, total, (r) => r.managerStatus === "Submitted"),
      finalized: percent(eligible, total, (r) => r.resultStatus === "Finalized"),
    },
    counts: {
      pendingApproval: eligible.filter((r) => r.sheetStatus === "Pending Approval").length,
      pendingHr: eligible.filter((r) => r.resultStatus === "Pending HR Review").length,
      finalized: finalized.length,
      notStarted: eligible.filter((r) => r.sheetStatus === "Not Started").length,
    },
    avgScore: avg(finalized.map((r) => r.performance.finalScore)),
    scoreBasis: `${finalized.length} finalized appraisal${finalized.length === 1 ? "" : "s"}`,
    exceptions: hr.exceptions || [],
    unmappedEmployees: (hr.unmapped_employees || []).map((e) => ({ id: e.employee_id, name: e.employee_name })),
    mappedCount: hr.mapped_count,
  });
}

async function managementFor(cycleId, lookups, bands) {
  const { data } = await api.get("/dashboard", { params: { view: "management", cycle_id: cycleId } });
  const dept = data["department-performance"]?.rows || [];
  const trend = (data.trend?.rows || []).find((r) => String(r.cycle_id) === String(cycleId)) || { eligible_count: 0, released_count: 0, average_score: null };
  const distribution = data["rating-distribution"]?.rows || [];
  const bandLabels = bands.map((b) => b.label);
  const labels = [...bandLabels, ...distribution.map((d) => d.rating).filter((r) => !bandLabels.includes(r))];
  return {
    headcount: trend.eligible_count,
    avgScore: trend.average_score === null ? null : Math.round(Number(trend.average_score) * 10) / 10,
    departments: dept.map((d) => ({
      id: String(d.department_id),
      name: lookups.departments.find((x) => x.id === String(d.department_id))?.name || `Department ${d.department_id}`,
      headcount: d.eligible_count,
      avgScore: d.average_score === null ? null : Math.round(Number(d.average_score) * 10) / 10,
    })),
    ratingDistribution: labels.map((label) => ({ rating: label, tone: toneForLabel(label, bands), count: distribution.find((d) => d.rating === label)?.count || 0 })),
    completion: {
      goalsApproved: null,
      selfSubmitted: null,
      managerSubmitted: null,
      finalized: trend.eligible_count ? Math.round((trend.released_count / trend.eligible_count) * 100) : 0,
    },
  };
}

export async function getManagementDashboard(cycleId) {
  const [lookups, cycles, cycle] = await Promise.all([getLookupsCached(), listCyclesCached(), cycleById(cycleId)]);
  const { bands } = await ratingConfigFor(cycle);
  const current = await managementFor(cycleId, lookups, bands);
  const older = cycles
    .filter((c) => c.id !== String(cycleId) && cycle && String(c.startDate) < String(cycle.startDate))
    .sort((a, b) => String(b.startDate).localeCompare(String(a.startDate)))[0];
  let previousCycle = null;
  if (older) {
    const prev = await managementFor(older.id, lookups, bands).catch(() => null);
    if (prev) previousCycle = { name: older.name, avgScore: prev.avgScore, ratingDistribution: prev.ratingDistribution, departments: prev.departments };
  }
  return ok({ ...current, previousCycle });
}

export async function getTeamCounters(cycleId) {
  const me = await getMe();
  const team = (await listRecords(cycleId)).filter((r) => r.review_manager_employee_id === me.employee_id && r.employee_id !== me.employee_id);
  return ok({
    pendingApprovals: team.filter((r) => r.current_stage === "GOAL_APPROVAL").length,
    pendingAssessments: team.filter((r) => r.current_stage === "MANAGER_ASSESSMENT").length,
  });
}
